import React, { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import GoogleLoginButton from '../components/GoogleLoginButton';
import { authAPI } from '../utils/api';
import { AuthCard, Field } from './SignInPage';

const SignUpPage = () => {
  const { isSignedIn, register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', password: '', password_confirmation: '', terms_version: '', privacy_version: '' });
  const [documents, setDocuments] = useState(null);
  const [optionsState, setOptionsState] = useState('loading');
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    authAPI.registrationOptions({ signal: controller.signal }).then(({ data }) => {
      setDocuments(data.data);
      setForm((current) => ({ ...current, terms_version: data.data.terms.version, privacy_version: data.data.privacy.version }));
      setOptionsState('ready');
    }).catch((requestError) => {
      if (requestError.code !== 'ERR_CANCELED') setOptionsState('error');
    });

    return () => controller.abort();
  }, []);

  if (isSignedIn) return <Navigate to="/dashboard" replace />;

  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const submit = async (event) => {
    event.preventDefault();
    if (!accepted) return setError('You must accept the Terms and Privacy Policy.');

    setSubmitting(true);
    setError('');
    try {
      await register(form);
      navigate('/dashboard', { replace: true });
    } catch (requestError) {
      const errors = requestError.response?.data?.errors;
      setError(errors ? Object.values(errors).flat()[0] : requestError.response?.data?.message || 'Unable to create your account.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthCard title="Create your account">
      <form onSubmit={submit} className="space-y-4">
        <GoogleLoginButton label="Sign up with Google" />
        <div className="flex items-center gap-3 text-xs uppercase tracking-wide text-gray-500"><span className="h-px flex-1 bg-gray-800" />or use email<span className="h-px flex-1 bg-gray-800" /></div>
        {optionsState === 'loading' && <p role="status" className="text-gray-300">Loading registration requirements...</p>}
        {optionsState === 'error' && <div role="alert" className="rounded border border-red-500 bg-red-950/50 p-3 text-red-200">Registration is temporarily unavailable because the current legal documents could not be loaded.</div>}
        {error && <div role="alert" className="rounded border border-red-500 bg-red-950/50 p-3 text-red-200">{error}</div>}
        <Field label="Full name" value={form.name} onChange={(value) => set('name', value)} autoComplete="name" />
        <Field label="Email" type="email" value={form.email} onChange={(value) => set('email', value)} autoComplete="email" />
        <Field label="Password" type="password" value={form.password} onChange={(value) => set('password', value)} autoComplete="new-password" />
        <Field label="Confirm password" type="password" value={form.password_confirmation} onChange={(value) => set('password_confirmation', value)} autoComplete="new-password" />
        <label className="flex items-start gap-2 text-sm text-gray-300">
          <input className="mt-1" type="checkbox" disabled={optionsState !== 'ready'} checked={accepted} onChange={(event) => setAccepted(event.target.checked)} />
          <span>I accept the <Link className="text-yellow-400" to={documents?.terms.path || '/terms'}>{documents?.terms.title || 'Terms'}</Link> and <Link className="text-yellow-400" to={documents?.privacy.path || '/privacy'}>{documents?.privacy.title || 'Privacy Policy'}</Link>.</span>
        </label>
        <button disabled={submitting || optionsState !== 'ready'} className="w-full rounded bg-yellow-400 px-4 py-3 font-bold text-black disabled:opacity-60">{submitting ? 'Creating account...' : 'Create account'}</button>
        <p className="text-center text-gray-400">Already registered? <Link className="text-yellow-400" to="/sign-in">Sign in</Link></p>
      </form>
    </AuthCard>
  );
};

export default SignUpPage;
